import {
  updateServerCapacityRequestSchema,
  type AccountCapacity,
  type ServerSummary,
} from "@woven/contracts";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Modal } from "../../components/ui/Modal";
import { useControlApiClient } from "../../lib/hooks";
import { useAccountCapacity } from "../capacity/AccountCapacityContext";
import { CapacityFields } from "./CapacityFields";
import { useCapacityDraft } from "./useCapacityDraft";
import { canEditServerCapacity, serverCapacity, temporaryServerCapacity } from "./serverCapacity";
import { CapacityTooltip } from "../capacity/CapacityTooltip";

export function EditServerCapacityModal({
  server,
  ready,
  onClose,
  onRefresh,
}: {
  server: ServerSummary;
  ready: boolean;
  onClose: () => void;
  onRefresh: () => void;
}) {
  const client = useControlApiClient();
  const { state: account, refresh } = useAccountCapacity();
  const current = serverCapacity(server);
  const budget =
    ready && account.status === "ready"
      ? Math.max(
          0,
          account.data.totalCapacity -
            Math.max(0, account.data.allocatedCapacity - (current.reservedCapacity ?? 0))
        )
      : null;
  const draft = useCapacityDraft(
    budget,
    current.pendingCapacity ?? {
      productType: current.productType,
      tickRateHz: current.tickRateHz,
      allocatedCcu: server.allocatedCcu ?? 1,
    },
    { clampToBudget: !current.pending }
  );
  const temporaryBudget =
    ready && account.status === "ready"
      ? account.data.availableCapacity + (current.reservedCapacity ?? 0)
      : null;
  const temporaryReservation =
    draft.capacity !== null
      ? temporaryServerCapacity(server, draft.tickRateHz, draft.capacity)
      : null;
  const headroomAvailable =
    temporaryBudget !== null &&
    temporaryReservation !== null &&
    temporaryReservation <= temporaryBudget;
  const budgetConflict =
    temporaryBudget !== null &&
    temporaryReservation !== null &&
    temporaryReservation > temporaryBudget;
  const exactPendingRequest =
    current.pendingCapacity !== undefined &&
    draft.productType === current.pendingCapacity.productType &&
    draft.tickRateHz === current.pendingCapacity.tickRateHz &&
    draft.allocatedCcu === current.pendingCapacity.allocatedCcu;
  const eligible = canEditServerCapacity(server);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fence, setFence] = useState<{ server: ServerSummary; account: AccountCapacity } | null>(
    null
  );
  const controller = useRef<AbortController | null>(null);
  useEffect(
    () => () => {
      controller.current?.abort();
    },
    []
  );
  const awaitingRefresh =
    fence !== null &&
    (!ready ||
      server === fence.server ||
      account.status !== "ready" ||
      account.data === fence.account);
  const input = updateServerCapacityRequestSchema.safeParse({
    generation: server.generation,
    productType: draft.productType,
    tickRateHz: draft.tickRateHz,
    allocatedCcu: draft.allocatedCcu,
  });
  const allowed =
    !busy &&
    ready &&
    account.status === "ready" &&
    input.success &&
    !awaitingRefresh &&
    eligible &&
    headroomAvailable &&
    (current.pending ? exactPendingRequest : draft.valid);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!allowed || !input.success || account.status !== "ready" || controller.current) return;
    const request = new AbortController();
    controller.current = request;
    setBusy(true);
    setMessage(null);
    setError(null);
    setFence({ server, account: account.data });
    try {
      const response = await client.updateServerCapacity(server.id, input.data, request.signal);
      if (request.signal.aborted) return;
      if (response.server.id !== server.id) throw new Error("Server scope changed");
      setMessage(
        "Capacity update accepted. Check refreshed configuration and live admission; capacity may remain reserved while connections drain."
      );
    } catch {
      if (!request.signal.aborted)
        setError(
          "Capacity update not confirmed. It may still complete. Refresh server status and account capacity before retrying; existing reservations may remain while draining."
        );
    } finally {
      if (!request.signal.aborted) {
        setBusy(false);
        refresh();
        onRefresh();
      }
      if (controller.current === request) controller.current = null;
    }
  }
  return (
    <Modal title="Edit Server Capacity" onClose={onClose} busy={busy}>
      <p className="form-note">
        Configure {server.name}. The budget includes this server's existing reservation, but
        excludes other servers' reservations.
      </p>
      <p className="form-note">
        Changes use the current server generation. Lower limits may drain gradually; no immediate
        kicks or capacity release are promised.
      </p>
      {account.status === "ready" && (
        <p className="form-note">
          Currently reserved: {current.reservedCapacity ?? "unknown"} units. Account available now:{" "}
          {account.data.availableCapacity} units. The calculation below previews the requested
          allocation; retained reservations may delay release.
        </p>
      )}
      <dl className="detail-list" aria-label="Capacity change comparison">
        <div className="detail-row">
          <dt>Prior configuration</dt>
          <dd>
            {current.productType} · {current.tickRateHz} Hz · {server.allocatedCcu ?? "—"} CCU ·{" "}
            {current.allocatedCapacity ?? "—"} units
          </dd>
        </div>
        <div className="detail-row">
          <dt>Requested configuration</dt>
          <dd>
            {current.pending && !current.pendingCapacity
              ? "Unavailable; refresh status to retrieve the pending request."
              : `${draft.productType} · ${draft.validRate ? draft.tickRateHz : "—"} Hz · ${draft.validCcu ? draft.allocatedCcu : "—"} CCU · ${draft.capacity ?? "—"} units`}
          </dd>
        </div>
        {current.pendingCapacity && (
          <div className="detail-row">
            <dt>Pending request</dt>
            <dd>
              {current.pendingCapacity.productType} · {current.pendingCapacity.tickRateHz} Hz ·{" "}
              {current.pendingCapacity.allocatedCcu} CCU ·{" "}
              {current.pendingCapacity.allocatedCapacity} units
            </dd>
          </div>
        )}
      </dl>
      {current.pending && (
        <p className={exactPendingRequest ? "form-note" : "form-error"}>
          A capacity change is pending. {current.reservedCapacity ?? "Unknown"} units remain
          reserved until the backend confirms release.
          {current.pendingCapacity
            ? " Only an exact retry of the pending request is allowed; different requests are disabled until it settles. Restore the pending values or refresh status."
            : " The pending target is unavailable. All updates are disabled until refresh retrieves it or confirms settlement."}
        </p>
      )}
      <form className="form" onSubmit={submit}>
        <CapacityFields
          draft={draft}
          budget={budget}
          disabled={busy || !ready || awaitingRefresh || !eligible}
          pendingRequest={current.pending}
          showCalculation={false}
          unavailable={
            account.status === "error"
              ? "Capacity unavailable. Refresh status before saving."
              : "Loading refreshed capacity and server status…"
          }
        />
        <section
          className="capacity-calculation"
          aria-label="Capacity calculation"
          aria-live="polite"
          aria-atomic="true"
        >
          <span className="field-label">
            Target Capacity{" "}
            <CapacityTooltip
              tickRateHz={draft.validRate ? draft.tickRateHz : 10}
              ccu={draft.validCcu ? draft.allocatedCcu : 25}
            />
          </span>
          <p className="metric-value">{draft.capacity ?? "—"} units requested</p>
          {temporaryBudget === null ? (
            <p className="form-note">
              {account.status === "error"
                ? "Capacity unavailable. Refresh status before saving."
                : "Capacity and server status must be refreshed before calculating headroom."}
            </p>
          ) : (
            <>
              <p className="form-note">
                Temporary reservation: {temporaryReservation ?? "—"} units. Headroom available:{" "}
                {temporaryBudget} units (
                {account.status === "ready" ? account.data.availableCapacity : "—"} remaining +{" "}
                {current.reservedCapacity ?? 0} currently reserved).
              </p>
              {temporaryReservation !== null &&
                draft.capacity !== null &&
                temporaryReservation > draft.capacity && (
                  <p className="form-note">
                    Temporary reservation exceeds the {draft.capacity}-unit target while the prior
                    CCU allocation drains. It covers max(current units, target units, prior CCU ×
                    requested Hz); release is not immediate.
                  </p>
                )}
              {headroomAvailable && (
                <p className="form-note">
                  {temporaryBudget - (temporaryReservation ?? 0)} units remaining during transition
                  {budget !== null && draft.capacity !== null
                    ? ` · ${Math.max(0, budget - draft.capacity)} units remaining after settlement`
                    : ""}
                  .
                </p>
              )}
            </>
          )}
          {!draft.validRate && (
            <p className="form-error">Enter a whole tick rate within the product bounds.</p>
          )}
          {!draft.validCcu && (
            <p className="form-error">Choose a whole CCU allocation from 1 to 4,096.</p>
          )}
          {budgetConflict && (
            <p className="form-error">
              Budget conflict: the temporary reservation needs {temporaryReservation} units, but
              only {temporaryBudget} are available. Free{" "}
              {(temporaryReservation ?? 0) - (temporaryBudget ?? 0)} capacity units or reduce the
              publish limit; lowering target CCU alone may not remove the prior CCU × requested Hz
              requirement.
            </p>
          )}
          {!current.pending &&
            budget !== null &&
            draft.capacity !== null &&
            draft.capacity > budget && (
              <p className="form-error">
                Budget conflict: the {draft.capacity}-unit target exceeds the {budget}-unit final
                allocation budget.
              </p>
            )}
          {current.pending &&
            exactPendingRequest &&
            budget !== null &&
            draft.capacity !== null &&
            draft.capacity > budget && (
              <p className="form-note">
                Budget conflict: the pending target exceeds the current final allocation budget.
                This is an already reserved pending request; an exact retry only resumes it. Review
                account allocations after settlement before starting a different request.
              </p>
            )}
          {eligible && draft.capacity !== null && temporaryReservation === null && (
            <p className="form-error">
              Prior allocation is unavailable; refresh status before a live update.
            </p>
          )}
        </section>
        {!server.generation && (
          <p className="form-error">
            Server generation is unavailable. Refresh status before editing capacity.
          </p>
        )}
        {!eligible && (
          <p className="form-error">
            Capacity changes are available only for unbound pending or running servers with a
            current generation and no deletion intent. Current status: {server.status}. Refresh
            status before editing.
          </p>
        )}
        {awaitingRefresh && (
          <p className="form-note" role="status">
            Waiting for refreshed server and account capacity before another update.
          </p>
        )}
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        {message && (
          <p className="form-note" role="status">
            {message}
          </p>
        )}
        <div className="server-actions">
          <button type="submit" className="btn btn--primary" disabled={!allowed}>
            {busy ? "Saving…" : current.pending ? "Retry Pending Capacity" : "Save Capacity"}
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            disabled={busy}
            onClick={() => {
              refresh();
              onRefresh();
            }}
          >
            Refresh Status
          </button>
          {current.pendingCapacity && !exactPendingRequest && (
            <button
              type="button"
              className="btn btn--ghost"
              disabled={busy || !ready || awaitingRefresh}
              onClick={() => {
                if (current.pendingCapacity) draft.setConfiguration(current.pendingCapacity);
              }}
            >
              Use Pending Request
            </button>
          )}
          <button type="button" className="btn btn--ghost" disabled={busy} onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}
